import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import {
  Video, Mic, Square, Clock,
  AlertTriangle, CheckCircle2, Loader2, ChevronRight, ChevronDown,
  Shield, Target, TrendingUp, XCircle, Zap, Eye,
  BarChart3, MessageSquare, Play, Award, RefreshCw, Download,
  MonitorX, Copy, PauseCircle, Chrome, Lightbulb, Volume2,
  CircleDot, FileText, Timer, Code2, Terminal, Ban
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import {
  getInterviewSession, startInterview, submitAnswer,
  reportViolation, completeInterview, getInterviewResults,
  uploadInterviewVideo, reportInterviewIssue, recomputeInterviewAnalysis
} from '../services/api';
import Editor from '@monaco-editor/react';
import VoiceCalibration from './VoiceCalibration';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const speakQuestion = (text) => {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) { resolve(); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.9; u.pitch = 1;
    u.onend = () => resolve();
    u.onerror = () => resolve();
    window.speechSynthesis.speak(u);
    setTimeout(resolve, 15000);
  });
};

const getSpeechRecognition = () => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  return SR ? new SR() : null;
};

// ===== SCORE RING COMPONENT =====
const ScoreRing = ({ score, size = 120, label, sublabel }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#22c55e' : score >= 65 ? '#3b82f6' : score >= 50 ? '#eab308' : '#ef4444';

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} stroke="currentColor" className="text-muted/20"
          strokeWidth="6" fill="none" />
        <circle cx={size/2} cy={size/2} r={radius} stroke={color}
          strokeWidth="6" fill="none" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
        <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="middle"
          className="text-2xl font-bold fill-current" transform={`rotate(90,${size/2},${size/2})`}>
          {Math.round(score)}
        </text>
      </svg>
      {label && <span className="text-xs font-medium text-muted-foreground">{label}</span>}
      {sublabel && <span className="text-[10px] text-muted-foreground/60">{sublabel}</span>}
    </div>
  );
};

// ===== SCORE BAR COMPONENT =====
const ScoreBar = ({ label, score, color = 'bg-primary' }) => {
  const barColor = score >= 80 ? 'bg-green-500' : score >= 65 ? 'bg-blue-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{Math.round(score)}/100</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`}
          style={{ width: `${score}%`, transition: 'width 1s ease-out' }} />
      </div>
    </div>
  );
};

// ===== QUESTION BREAKDOWN CARD =====
const QuestionCard = ({ q, index, sessionId, hasVideo }) => {
  const [open, setOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const scoreColor = q.score >= 80 ? 'text-green-500' : q.score >= 65 ? 'text-blue-500' : q.score >= 50 ? 'text-yellow-500' : 'text-red-500';
  const categoryLabel = q.category === 'technical' ? 'Technical' : q.category === 'work_setup' ? 'Work Setup' : q.category === 'coding' ? 'Coding' : 'Culture';
  const categoryColor = q.category === 'technical' ? 'bg-violet-500/10 text-violet-500 border-violet-500/20' :
                         q.category === 'work_setup' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' :
                         q.category === 'coding' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' :
                         'bg-teal-500/10 text-teal-500 border-teal-500/20';

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center gap-3 p-3 rounded-lg border hover:border-primary/30 cursor-pointer transition-all group"
          data-testid={`question-breakdown-${index}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 ${scoreColor} border-current`}>
            <span className="text-sm font-bold">{q.score}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <Badge className={`${categoryColor} text-[10px] px-1.5 py-0`}>{categoryLabel}</Badge>
              <span className="text-xs text-muted-foreground">Q{q.question_number}</span>
              {q.time_taken && (
                <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                  <Clock className="h-3 w-3" />{q.time_taken}s
                </span>
              )}
            </div>
            <p className="text-sm line-clamp-2 break-words">{q.question}</p>
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-4 border border-t-0 rounded-b-lg -mt-1 space-y-4 bg-muted/20 overflow-hidden">
          {/* Full Question */}
          <div>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Question</span>
            <p className="text-sm mt-1 leading-relaxed break-words">{q.question}</p>
          </div>

          {/* Answer */}
          <div>
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Candidate's Answer</span>
            <p className="text-sm mt-1 leading-relaxed bg-background p-3 rounded-md border italic break-words whitespace-pre-wrap">
              "{q.answer_text || 'No answer recorded'}"
            </p>
          </div>

          {/* Scores */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-2 bg-background rounded-md border">
              <p className="text-lg font-bold">{q.relevance ?? '—'}</p>
              <p className="text-[10px] text-muted-foreground">Relevance</p>
            </div>
            <div className="text-center p-2 bg-background rounded-md border">
              <p className="text-lg font-bold">{q.clarity ?? '—'}</p>
              <p className="text-[10px] text-muted-foreground">Clarity</p>
            </div>
            <div className="text-center p-2 bg-background rounded-md border">
              <p className="text-lg font-bold">{q.depth ?? '—'}</p>
              <p className="text-[10px] text-muted-foreground">Depth</p>
            </div>
          </div>

          {/* Feedback */}
          {q.feedback && (
            <div className="p-3 bg-primary/5 border border-primary/10 rounded-lg">
              <span className="text-xs font-medium text-primary">AI Feedback</span>
              <p className="text-sm mt-1 break-words whitespace-pre-wrap">{q.feedback}</p>
            </div>
          )}

          {/* Key points & Missing */}
          <div className="grid grid-cols-2 gap-3">
            {q.key_points_covered?.length > 0 && (
              <div>
                <span className="text-xs text-green-500 font-medium flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Covered
                </span>
                <ul className="mt-1 space-y-0.5">
                  {q.key_points_covered.map((p, i) => (
                    <li key={i} className="text-xs text-muted-foreground">{p}</li>
                  ))}
                </ul>
              </div>
            )}
            {q.missing_points?.length > 0 && (
              <div>
                <span className="text-xs text-red-500 font-medium flex items-center gap-1">
                  <XCircle className="h-3 w-3" /> Missing
                </span>
                <ul className="mt-1 space-y-0.5">
                  {q.missing_points.map((p, i) => (
                    <li key={i} className="text-xs text-muted-foreground">{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Video playback */}
          {hasVideo && (
            <div>
              <Button variant="outline" size="sm" onClick={() => setShowVideo(v => !v)}
                className="gap-1" data-testid={`play-video-${index}`}>
                {showVideo ? <Eye className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                {showVideo ? 'Hide Video' : 'Watch Recording'}
              </Button>
              {showVideo && (
                <div className="mt-2 rounded-lg overflow-hidden border bg-black">
                  <video
                    controls
                    className="w-full max-h-[300px]"
                    src={`${API_BASE}/api/interview/${sessionId}/video/${index}`}
                    onError={(e) => {
                      const parent = e.target.parentElement;
                      if (parent) {
                        e.target.style.display = 'none';
                        const msg = document.createElement('div');
                        msg.className = 'flex items-center justify-center py-8 text-sm text-muted-foreground';
                        msg.textContent = 'Recording not available for this question';
                        parent.appendChild(msg);
                      }
                    }}
                  >
                    Your browser does not support video playback.
                  </video>
                </div>
              )}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};


// ===== THANK YOU PAGE (when results are hidden) =====
const ThankYouPage = ({ session, candidateName, jobTitle }) => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="thank-you-page">
      <Toaster richColors position="top-right" />
      <div className="max-w-lg w-full text-center space-y-8">
        {/* Success animation circle */}
        <div className="relative mx-auto w-28 h-28">
          <div className="absolute inset-0 rounded-full bg-green-500/10 animate-ping" style={{ animationDuration: '2s' }} />
          <div className="relative w-28 h-28 rounded-full bg-green-500/15 flex items-center justify-center border-2 border-green-500/30">
            <CheckCircle2 className="h-14 w-14 text-green-500" strokeWidth={1.5} />
          </div>
        </div>

        {/* Main heading */}
        <div className="space-y-3">
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="thank-you-heading">
            Thank You, {candidateName || 'Candidate'}!
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Your interview for <span className="font-medium text-foreground">{jobTitle}</span> has been completed successfully.
          </p>
        </div>

        {/* What happens next card */}
        <Card className="text-left">
          <CardContent className="pt-6 space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">What Happens Next</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-blue-500">1</span>
                </div>
                <div>
                  <p className="text-sm font-medium">Review in Progress</p>
                  <p className="text-xs text-muted-foreground">Our hiring team is carefully reviewing your responses and evaluating your performance.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-indigo-500">2</span>
                </div>
                <div>
                  <p className="text-sm font-medium">We'll Be in Touch</p>
                  <p className="text-xs text-muted-foreground">A member of our hiring team will contact you with the next steps and feedback.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-sm font-bold text-green-500">3</span>
                </div>
                <div>
                  <p className="text-sm font-medium">Stay Connected</p>
                  <p className="text-xs text-muted-foreground">Keep an eye on your email for updates. We appreciate your interest and patience.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer message */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            We appreciate the time and effort you put into this interview.
          </p>
          <p className="text-xs text-muted-foreground/60">
            You may now close this window.
          </p>
        </div>
      </div>
    </div>
  );
};

// ===== COMPREHENSIVE RESULTS PAGE =====
const VIOLATION_CONFIG = {
  tab_switch: { label: 'Tab Switch', icon: 'MonitorX', color: 'text-red-500', bg: 'bg-red-500/5 border-red-500/10' },
  window_blur: { label: 'Window Focus Lost', icon: 'Eye', color: 'text-orange-500', bg: 'bg-orange-500/5 border-orange-500/10' },
  copy: { label: 'Copy Attempt', icon: 'Copy', color: 'text-red-500', bg: 'bg-red-500/5 border-red-500/10' },
  paste: { label: 'Paste Attempt', icon: 'Copy', color: 'text-red-600', bg: 'bg-red-600/5 border-red-600/10' },
  cut: { label: 'Cut Attempt', icon: 'Copy', color: 'text-red-500', bg: 'bg-red-500/5 border-red-500/10' },
  right_click: { label: 'Right Click', icon: 'Ban', color: 'text-amber-500', bg: 'bg-amber-500/5 border-amber-500/10' },
  keystroke_burst: { label: 'Rapid Typing', icon: 'Zap', color: 'text-purple-500', bg: 'bg-purple-500/5 border-purple-500/10' },
  devtools_open: { label: 'DevTools Detected', icon: 'Terminal', color: 'text-red-600', bg: 'bg-red-600/5 border-red-600/10' },
  ai_content_suspected: { label: 'AI Content Suspected', icon: 'AlertTriangle', color: 'text-rose-500', bg: 'bg-rose-500/5 border-rose-500/10' },
};

const ViolationIcon = ({ type }) => {
  const iconMap = {
    MonitorX: MonitorX, Eye: Eye, Copy: Copy, Ban: Ban,
    Zap: Zap, Terminal: Terminal, AlertTriangle: AlertTriangle,
  };
  const cfg = VIOLATION_CONFIG[type] || VIOLATION_CONFIG.tab_switch;
  const IconComp = iconMap[cfg.icon] || AlertTriangle;
  return <IconComp className={`h-4 w-4 ${cfg.color} shrink-0`} />;
};

const ResultsPage = ({ session, results, onRecompute, token }) => {
  const a = results?.analysis || {};
  const breakdown = a.question_breakdown || [];
  const violations = results?.proctoring_violations || 0;
  const violationLog = results?.violation_log || [];
  const aiSummary = results?.ai_detection_summary || {};
  const hasVideos = results?.has_videos;
  const recColor = a.recommendation === 'Strongly Recommend' ? 'bg-green-500' :
                   a.recommendation === 'Recommend' ? 'bg-blue-500' :
                   a.recommendation === 'Consider' ? 'bg-yellow-500' : 'bg-red-500';

  // Group violations by type for summary
  const violationGroups = {};
  violationLog.forEach(v => {
    const t = v.type || 'unknown';
    if (!violationGroups[t]) violationGroups[t] = 0;
    violationGroups[t]++;
  });

  // Per-answer AI detection from responses
  const perAnswerAI = (results?.responses || []).map((r, i) => ({
    question: r.question?.substring(0, 80),
    questionNum: i + 1,
    ...r.ai_detection
  })).filter(r => r.ai_suspicion_score !== undefined);

  const aiMaxScore = aiSummary.max_score || (perAnswerAI.length > 0 ? Math.max(...perAnswerAI.map(a => a.ai_suspicion_score)) : 0);
  const aiRiskLevel = aiMaxScore >= 60 ? 'High Risk' : aiMaxScore >= 30 ? 'Medium Risk' : 'Low Risk';
  const aiRiskColor = aiMaxScore >= 60 ? 'text-red-500' : aiMaxScore >= 30 ? 'text-amber-500' : 'text-green-500';
  const aiRiskBg = aiMaxScore >= 60 ? 'bg-red-500' : aiMaxScore >= 30 ? 'bg-amber-500' : 'bg-green-500';

  // Integrity Score
  const integrity = results?.integrity_score || {};
  const integrityScore = integrity.score ?? 100;
  const integrityLabel = integrity.label || 'Trusted';
  const integrityDeductions = integrity.deductions || [];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Toaster richColors position="top-right" />

      {/* Hero header */}
      <div className="border-b bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex items-center gap-4">
              <ScoreRing score={a.overall_score || 0} size={130} />
              <ScoreRing
                score={integrityScore}
                size={100}
                label="Integrity"
                sublabel={integrityLabel}
              />
            </div>
            <div className="flex-1 text-center md:text-left min-w-0">
              <h1 className="text-2xl font-bold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="results-candidate-name">
                {results?.candidate_name || 'Candidate'}
              </h1>
              <p className="text-muted-foreground truncate">{results?.job_title}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3 justify-center md:justify-start">
                <Badge className={`${recColor} text-white px-3 py-1`} data-testid="results-recommendation">
                  <Award className="h-3.5 w-3.5 mr-1" />
                  {a.recommendation || 'Pending'}
                </Badge>
                <Badge variant="outline" data-testid="results-fit-badge">
                  {a.fit_assessment || 'Pending'}
                </Badge>
                {violations > 0 && (
                  <Badge variant="destructive" className="gap-1" data-testid="results-violations-badge">
                    <AlertTriangle className="h-3 w-3" />
                    {violations} Violation{violations > 1 ? 's' : ''}
                  </Badge>
                )}
                {aiMaxScore >= 30 && (
                  <Badge className={`${aiRiskBg} text-white gap-1`} data-testid="results-ai-risk-badge">
                    <Shield className="h-3 w-3" />
                    AI: {aiRiskLevel}
                  </Badge>
                )}
                {results?.attempt_number > 1 && (
                  <Badge variant="secondary">Attempt #{results.attempt_number}</Badge>
                )}
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 ml-1"
                  onClick={() => { window.open(`${API_BASE}/api/interview/${token}/report-pdf`, '_blank'); }}
                  data-testid="download-report-btn">
                  <Download className="h-3.5 w-3.5" /> Download Report
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-muted-foreground justify-center md:justify-start">
                {a.total_questions && <span>{a.total_questions} questions answered</span>}
                {a.total_time_seconds && <span>{Math.round(a.total_time_seconds / 60)} min total</span>}
                {a.avg_time_per_question && <span>~{Math.round(a.avg_time_per_question)}s avg/question</span>}
                {onRecompute && (!a.strengths?.length && !a.fit_narrative) && (
                  <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1 ml-2"
                    onClick={onRecompute} data-testid="recompute-analysis-btn">
                    <RefreshCw className="h-3 w-3" /> Recompute Analysis
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Category Scores */}
        <Card data-testid="results-scores-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <BarChart3 className="h-5 w-5 text-primary" strokeWidth={1.5} />
              Performance Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-6">
              <ScoreRing score={a.technical_score || 0} size={100} label="Technical" />
              <ScoreRing score={a.communication_score || 0} size={100} label="Communication" />
              <ScoreRing score={a.work_setup_score || 0} size={100} label="Work Setup" />
            </div>
            <Separator className="my-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ScoreBar label="Answer Relevance" score={a.relevance_score || 0} />
              <ScoreBar label="Response Depth" score={a.depth_score || 0} />
              <ScoreBar label="Clarity of Speech" score={a.clarity_score || 0} />
            </div>
          </CardContent>
        </Card>

        {/* Fit Narrative — Why Fit / Why Not Fit */}
        {(a.fit_narrative || a.not_fit_reasons || a.overall_verdict) && (
          <Card data-testid="results-fit-narrative-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Target className="h-5 w-5 text-primary" strokeWidth={1.5} />
                Resume + Interview Verdict
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {a.overall_verdict && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <p className="text-sm font-medium leading-relaxed">{a.overall_verdict}</p>
                </div>
              )}
              {a.resume_interview_alignment && (
                <div className="p-3 bg-muted/40 rounded-lg">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Resume vs Interview Alignment</span>
                  <p className="text-sm mt-1 leading-relaxed">{a.resume_interview_alignment}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {a.fit_narrative && (
                  <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                    <span className="text-xs font-medium text-green-500 flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Why This Candidate IS a Fit
                    </span>
                    <p className="text-sm mt-2 leading-relaxed">{a.fit_narrative}</p>
                  </div>
                )}
                {a.not_fit_reasons && (
                  <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                    <span className="text-xs font-medium text-red-500 flex items-center gap-1">
                      <XCircle className="h-3.5 w-3.5" /> Concerns & Gaps
                    </span>
                    <p className="text-sm mt-2 leading-relaxed">{a.not_fit_reasons}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Strengths & Improvements side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Green Flags + Strengths */}
          <Card data-testid="results-strengths-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-green-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} />
                Strengths & Green Flags
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(a.strengths || []).map((s, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Zap className="h-4 w-4 text-green-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-sm">{s}</span>
                  </div>
                ))}
                {(a.green_flags || []).map((f, i) => (
                  <div key={`g${i}`} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-sm">{f}</span>
                  </div>
                ))}
                {(!a.strengths?.length && !a.green_flags?.length) && (
                  <p className="text-sm text-muted-foreground">No strengths identified</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Red Flags + Areas to Improve + Missing */}
          <Card data-testid="results-improvements-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-red-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <TrendingUp className="h-5 w-5" strokeWidth={1.5} />
                What Was Missing & Areas to Improve
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(a.missing_areas || []).map((m, i) => (
                  <div key={`m${i}`} className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-sm">{m}</span>
                  </div>
                ))}
                {(a.areas_to_improve || []).map((a2, i) => (
                  <div key={`a${i}`} className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-sm">{a2}</span>
                  </div>
                ))}
                {(a.red_flags || []).map((f, i) => (
                  <div key={`r${i}`} className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <span className="text-sm text-red-400">{f}</span>
                  </div>
                ))}
                {(!a.missing_areas?.length && !a.areas_to_improve?.length && !a.red_flags?.length) && (
                  <p className="text-sm text-muted-foreground">No significant gaps identified</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Detection Analysis */}
        {perAnswerAI.length > 0 && (
          <Card data-testid="results-ai-detection-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Shield className="h-5 w-5 text-primary" strokeWidth={1.5} />
                AI Content Detection
                <Badge className={`${aiRiskBg} text-white text-xs ml-auto`}>{aiRiskLevel}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-muted/30 rounded-lg border">
                  <p className={`text-2xl font-bold ${aiRiskColor}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {aiMaxScore}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">Peak Suspicion</p>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg border">
                  <p className="text-2xl font-bold text-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {aiSummary.avg_score || (perAnswerAI.length > 0 ? Math.round(perAnswerAI.reduce((s, a) => s + a.ai_suspicion_score, 0) / perAnswerAI.length) : 0)}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">Avg Suspicion</p>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg border">
                  <p className="text-2xl font-bold text-red-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {aiSummary.high_risk_answers || perAnswerAI.filter(a => a.ai_suspicion_score >= 60).length}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">High Risk Answers</p>
                </div>
              </div>

              {/* Per-answer breakdown */}
              <div className="space-y-2">
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Per-Answer Analysis</span>
                {perAnswerAI.map((item, i) => {
                  const sc = item.ai_suspicion_score;
                  const barColor = sc >= 60 ? 'bg-red-500' : sc >= 30 ? 'bg-amber-500' : 'bg-green-500';
                  const riskLabel = sc >= 60 ? 'High' : sc >= 30 ? 'Medium' : 'Low';
                  return (
                    <div key={i} className="p-2 rounded-md border bg-muted/10" data-testid={`ai-detection-q${item.questionNum}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium">Q{item.questionNum}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant={sc >= 60 ? 'destructive' : 'secondary'} className="text-[10px] h-4">{riskLabel}</Badge>
                          <span className="text-xs font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{sc}/100</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${sc}%`, transition: 'width 0.8s ease-out' }} />
                      </div>
                      {item.ai_flags?.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {item.ai_flags.map((f, fi) => (
                            <span key={fi} className="text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">{f}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Proctoring / Violations */}
        {violations > 0 && (
          <Card data-testid="results-violations-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-destructive" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Shield className="h-5 w-5" strokeWidth={1.5} />
                Proctoring Violations ({violations})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Violation type summary */}
              {Object.keys(violationGroups).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(violationGroups).map(([type, count]) => {
                    const cfg = VIOLATION_CONFIG[type] || { label: type, color: 'text-red-500', bg: 'bg-red-500/5 border-red-500/10' };
                    return (
                      <Badge key={type} variant="outline" className={`text-xs gap-1 ${cfg.bg} ${cfg.color} border`}
                        data-testid={`violation-type-${type}`}>
                        <ViolationIcon type={type} />
                        {cfg.label}: {count}
                      </Badge>
                    );
                  })}
                </div>
              )}

              {/* Detailed timeline */}
              <div className="space-y-1.5">
                {violationLog.map((v, i) => {
                  const cfg = VIOLATION_CONFIG[v.type] || { label: v.type || 'Unknown', color: 'text-red-500', bg: 'bg-red-500/5 border-red-500/10' };
                  return (
                    <div key={i} className={`flex items-start gap-3 p-2 rounded-md border ${cfg.bg}`}
                      data-testid={`violation-entry-${i}`}>
                      <ViolationIcon type={v.type} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${cfg.color}`}>{cfg.label}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : ''}
                          </span>
                        </div>
                        {v.details && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{v.details}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {violationLog.length === 0 && (
                <p className="text-sm text-muted-foreground">{violations} violation(s) recorded during the interview</p>
              )}
            </CardContent>
          </Card>
        )}


        {/* Interview Integrity Score Breakdown */}
        {integrityDeductions.length > 0 && (
          <Card data-testid="results-integrity-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Shield className="h-5 w-5 text-primary" strokeWidth={1.5} />
                Interview Integrity Score
                <Badge
                  className={`ml-auto text-white text-xs ${integrityScore >= 80 ? 'bg-green-500' : integrityScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                  data-testid="integrity-score-badge"
                >
                  {integrityScore}/100 — {integrityLabel}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${integrityScore >= 80 ? 'bg-green-500' : integrityScore >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${integrityScore}%`, transition: 'width 1s ease-out' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded-md border bg-muted/20 text-center">
                  <p className="text-sm font-bold text-red-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    -{integrity.violation_deduction || 0}
                  </p>
                  <p className="text-[10px] text-muted-foreground">Violation Penalty</p>
                </div>
                <div className="p-2 rounded-md border bg-muted/20 text-center">
                  <p className="text-sm font-bold text-orange-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    -{integrity.ai_deduction || 0}
                  </p>
                  <p className="text-[10px] text-muted-foreground">AI Suspicion Penalty</p>
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Deduction Details</span>
                {integrityDeductions.map((d, i) => (
                  <div key={i} className="flex items-center justify-between text-sm p-1.5 rounded-md bg-muted/10 border">
                    <div className="flex items-center gap-2">
                      {d.category === 'violation' ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                      ) : (
                        <Shield className="h-3.5 w-3.5 text-orange-400 shrink-0" />
                      )}
                      <span className="text-muted-foreground text-xs">{d.reason}</span>
                    </div>
                    <span className="text-red-400 font-bold text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{d.points}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Per-Question Breakdown */}
        <Card data-testid="results-questions-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <MessageSquare className="h-5 w-5 text-primary" strokeWidth={1.5} />
              Question-by-Question Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {breakdown.map((q, idx) => (
                <QuestionCard key={idx} q={q} index={idx} sessionId={results?.session_id} hasVideo={hasVideos} />
              ))}
              {breakdown.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No question breakdown available</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Key Points Covered */}
        {a.key_points_covered?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Target className="h-5 w-5 text-primary" strokeWidth={1.5} />
                Key Points Covered Across All Answers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {a.key_points_covered.map((p, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{p}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-sm text-muted-foreground pb-8">
          Interview completed on {results?.completed_at ? new Date(results.completed_at).toLocaleString() : 'N/A'}
        </p>
      </div>
    </div>
  );
};


// ===== MAIN INTERVIEW PAGE =====
export const InterviewPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const isRecruiterView = searchParams.get('recruiter') === '1';

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candidateInfo, setCandidateInfo] = useState({ candidate_name: '', candidate_email: '', candidate_phone: '' });

  const [stage, setStage] = useState('info');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [resumeFromIndex, setResumeFromIndex] = useState(0);
  const currentQuestion = session?.questions?.[currentQuestionIndex];
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [typedAnswer, setTypedAnswer] = useState('');
  const [showTypeFallback, setShowTypeFallback] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const [termsAgreed, setTermsAgreed] = useState(false);

  // Device check state
  const [cameraOk, setCameraOk] = useState(false);
  const [micOk, setMicOk] = useState(false);
  const [speechOk, setSpeechOk] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [speechTestResult, setSpeechTestResult] = useState('');
  const [speechTesting, setSpeechTesting] = useState(false);
  const [deviceCheckStream, setDeviceCheckStream] = useState(null);
  const [speechRetryCount, setSpeechRetryCount] = useState(0);
  const [issueText, setIssueText] = useState('');
  const [reportingIssue, setReportingIssue] = useState(false);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const deviceVideoRef = useRef(null);
  const micAnalyserRef = useRef(null);
  const micAnimFrameRef = useRef(null);

  const [hasPermissions, setHasPermissions] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');
  const accumulatedTranscriptRef = useRef('');
  const isRecordingRef = useRef(false);
  const questionStartTimeRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const speechDetectedRef = useRef(false);
  const fallbackTimerRef = useRef(null);

  const [violations, setViolations] = useState(0);
  const [showViolationWarning, setShowViolationWarning] = useState(false);

  // Coding lab state
  const [codeEditorValue, setCodeEditorValue] = useState('');
  const [codeLanguage, setCodeLanguage] = useState('python');
  const [codeOutput, setCodeOutput] = useState('');
  const [codeRunning, setCodeRunning] = useState(false);
  const [copyPasteViolations, setCopyPasteViolations] = useState(0);

  // Answer review state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewTimeTaken, setReviewTimeTaken] = useState(0);
  const stoppedVideoChunksRef = useRef([]);
  const stoppedMimeTypeRef = useRef('');

  // Voice calibration state
  const [voiceCalibration, setVoiceCalibration] = useState(null);
  const calibrationLangRef = useRef('en-US');

  useEffect(() => { if (token) fetchSession(); }, [token]);

  useEffect(() => {
    if (stage !== 'interview') return;
    const h = () => { if (document.hidden) handleViolation('tab_switch', 'Switched away from interview tab'); };
    document.addEventListener('visibilitychange', h);
    return () => document.removeEventListener('visibilitychange', h);
  }, [stage]);

  // Window blur detection (tracks time away)
  const blurStartRef = useRef(null);
  const totalTimeAwayRef = useRef(0);
  useEffect(() => {
    if (stage !== 'interview') return;
    const onBlur = () => { blurStartRef.current = Date.now(); };
    const onFocus = () => {
      if (blurStartRef.current) {
        const away = Math.round((Date.now() - blurStartRef.current) / 1000);
        totalTimeAwayRef.current += away;
        blurStartRef.current = null;
        if (away > 2) {
          handleViolation('window_blur', `Window lost focus for ${away}s (total away: ${totalTimeAwayRef.current}s)`);
        }
      }
    };
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => { window.removeEventListener('blur', onBlur); window.removeEventListener('focus', onFocus); };
  }, [stage]);

  // Copy/Paste/Cut + Right-click + Keystroke burst detection
  const lastKeystrokeRef = useRef([]);
  useEffect(() => {
    if (stage !== 'interview') return;

    const preventCopyPaste = (e) => {
      const type = e.type;
      e.preventDefault();
      setCopyPasteViolations(prev => prev + 1);
      const clipText = type === 'paste' ? (e.clipboardData?.getData('text') || '').substring(0, 100) : '';
      handleViolation(type, clipText ? `Attempted ${type} with content: "${clipText}..."` : `Attempted ${type}`);
      toast.warning(`${type.charAt(0).toUpperCase() + type.slice(1)} is blocked during the interview. This violation has been recorded.`);
    };

    const preventContext = (e) => {
      e.preventDefault();
      handleViolation('right_click', 'Attempted to open context menu');
      toast.warning('Right-click is disabled during the interview.');
    };

    // Keystroke burst detection: flag if >12 chars appear in <800ms (inhuman typing speed)
    const detectKeystrokeBurst = (e) => {
      if (!e.key || e.key.length > 1) return; // ignore special keys
      const now = Date.now();
      lastKeystrokeRef.current.push(now);
      // Keep only last 15 keystrokes
      if (lastKeystrokeRef.current.length > 15) lastKeystrokeRef.current.shift();
      const keystrokes = lastKeystrokeRef.current;
      if (keystrokes.length >= 12) {
        const elapsed = keystrokes[keystrokes.length - 1] - keystrokes[keystrokes.length - 12];
        if (elapsed < 800) {
          handleViolation('keystroke_burst', `Rapid typing detected: 12 chars in ${elapsed}ms (possible paste bypass)`);
          toast.warning('Unusually fast typing detected. This has been recorded.');
          lastKeystrokeRef.current = [];
        }
      }
    };

    document.addEventListener('copy', preventCopyPaste);
    document.addEventListener('paste', preventCopyPaste);
    document.addEventListener('cut', preventCopyPaste);
    document.addEventListener('contextmenu', preventContext);
    document.addEventListener('keydown', detectKeystrokeBurst);

    return () => {
      document.removeEventListener('copy', preventCopyPaste);
      document.removeEventListener('paste', preventCopyPaste);
      document.removeEventListener('cut', preventCopyPaste);
      document.removeEventListener('contextmenu', preventContext);
      document.removeEventListener('keydown', detectKeystrokeBurst);
    };
  }, [stage]);

  // DevTools detection (timing-based)
  useEffect(() => {
    if (stage !== 'interview') return;
    const devToolsDetected = useRef ? false : false;
    let detectionCount = 0;
    const checkDevTools = () => {
      const start = performance.now();
      debugger; // This line pauses when DevTools is open
      const end = performance.now();
      if (end - start > 100 && detectionCount < 3) {
        detectionCount++;
        handleViolation('devtools_open', 'Browser DevTools detected open');
        toast.error('Developer Tools detected! This is a serious violation.');
      }
    };
    // Use image-based detection instead (safer, no debugger)
    const detectInterval = setInterval(() => {
      const threshold = 160;
      if (window.outerWidth - window.innerWidth > threshold || window.outerHeight - window.innerHeight > threshold) {
        if (detectionCount < 3) {
          detectionCount++;
          handleViolation('devtools_open', 'Browser DevTools panel detected (window size anomaly)');
          toast.error('Developer Tools detected! This is a serious violation and has been recorded.');
        }
      }
    }, 5000);
    return () => clearInterval(detectInterval);
  }, [stage]);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRecording]);

  useEffect(() => {
    if (videoRef.current && mediaStream) videoRef.current.srcObject = mediaStream;
  }, [mediaStream, stage]);

  useEffect(() => {
    return () => {
      if (mediaStream) mediaStream.getTracks().forEach(t => t.stop());
      window.speechSynthesis?.cancel();
      if (recognitionRef.current) try { recognitionRef.current.stop(); } catch(e) {}
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, [mediaStream]);

  // ===== DEVICE CHECK FUNCTIONS =====
  const startDeviceCheck = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setDeviceCheckStream(stream);
      
      // Camera check
      if (deviceVideoRef.current) {
        deviceVideoRef.current.srcObject = stream;
      }
      setCameraOk(true);
      
      // Mic level meter
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      micAnalyserRef.current = { analyser, audioCtx };
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const normalized = Math.min(100, (avg / 128) * 100);
        setMicLevel(normalized);
        if (normalized > 5) setMicOk(true);
        micAnimFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (err) {
      toast.error('Could not access camera/microphone. Please check permissions.');
    }
  };

  const testSpeechRecognition = () => {
    setSpeechTesting(true);
    setSpeechTestResult('');
    const recognition = getSpeechRecognition();
    
    if (!recognition) {
      setSpeechTesting(false);
      setSpeechTestResult('NOT_SUPPORTED');
      return;
    }

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    let finalText = '';

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      finalText = transcript;
      setSpeechTestResult(transcript);
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech') {
        setSpeechTestResult('NO_SPEECH');
      } else {
        setSpeechTestResult('ERROR: ' + event.error);
      }
      setSpeechTesting(false);
    };

    recognition.onend = () => {
      setSpeechTesting(false);
      if (finalText.trim().length > 0) {
        setSpeechOk(true);
      }
    };

    recognition.start();
    // Auto-stop after 6 seconds
    setTimeout(() => {
      try { recognition.stop(); } catch (e) {}
    }, 6000);
  };

  const cleanupDeviceCheck = () => {
    if (micAnimFrameRef.current) cancelAnimationFrame(micAnimFrameRef.current);
    if (micAnalyserRef.current?.audioCtx) {
      try { micAnalyserRef.current.audioCtx.close(); } catch (e) {}
    }
    if (deviceCheckStream) {
      deviceCheckStream.getTracks().forEach(t => t.stop());
      setDeviceCheckStream(null);
    }
  };

  const handleDeviceCheckProceed = () => {
    cleanupDeviceCheck();
    setStage('permissions');
  };

  const handleReportIssue = async () => {
    if (!issueText.trim()) { toast.error('Please describe the issue'); return; }
    try {
      setReportingIssue(true);
      const browserInfo = navigator.userAgent;
      await reportInterviewIssue(token, {
        candidate_name: candidateInfo.candidate_name || 'Unknown',
        issue_description: issueText.trim(),
        browser: browserInfo,
        device_info: `${navigator.platform} | ${window.screen.width}x${window.screen.height}`
      });
      toast.success('Issue reported to the recruiter. They will get back to you.');
      setShowIssueForm(false);
      setIssueText('');
    } catch (e) {
      toast.error('Failed to report issue');
    } finally {
      setReportingIssue(false);
    }
  };

  // Start device check when entering that stage
  useEffect(() => {
    if (stage === 'device_check') {
      startDeviceCheck();
    }
    return () => {
      if (stage === 'device_check') {
        if (micAnimFrameRef.current) cancelAnimationFrame(micAnimFrameRef.current);
      }
    };
  }, [stage]);

  const fetchSession = async () => {
    try {
      setLoading(true);
      const data = await getInterviewSession(token);
      setSession(data);
      if (data.status === 'completed') {
        setStage('completed');
        try { const r = await getInterviewResults(token); setResults(r); } catch(e) {}
      } else if (data.candidate_name) {
        setCandidateInfo({ candidate_name: data.candidate_name || '', candidate_email: data.candidate_email || '', candidate_phone: data.candidate_phone || '' });
        if (data.status === 'in_progress') {
          // Check how many UNIQUE questions were answered
          const answeredIds = new Set((data.responses || []).map(r => r.question_id));
          const totalQuestions = (data.questions || []).length;
          if (answeredIds.size > 0 && answeredIds.size < totalQuestions) {
            // Partially answered — resume from next unanswered
            setResumeFromIndex(answeredIds.size);
          }
          // Even if all questions answered, let the candidate click through naturally
          // Don't auto-complete on page load
          setStage('permissions');
        }
      }
    } catch (err) { setError('Interview session not found or has expired'); }
    finally { setLoading(false); }
  };

  const handleViolation = async (type = 'tab_switch', details = '') => {
    setViolations(prev => prev + 1);
    setShowViolationWarning(true);
    try { await reportViolation(token, type, details); } catch(e) {}
  };

  const requestPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setMediaStream(stream);
      setHasPermissions(true);
      toast.success('Camera and microphone access granted');
      return true;
    } catch (err) { toast.error('Please allow camera and microphone access'); return false; }
  };

  const handleStartInterview = async () => {
    if (!candidateInfo.candidate_name || !candidateInfo.candidate_email || !candidateInfo.candidate_phone) {
      toast.error('Please fill in all fields'); return;
    }
    try { await startInterview(token, candidateInfo); setStage('terms'); }
    catch (err) { toast.error('Failed to start interview'); }
  };

  const handlePermissionsGranted = async () => {
    const granted = await requestPermissions();
    if (granted) { setStage('voice_calibration'); }
  };

  const handleCalibrationComplete = (calibrationData) => {
    setVoiceCalibration(calibrationData);
    calibrationLangRef.current = calibrationData?.best_language || 'en-US';
    setStage('interview');
    const startIdx = resumeFromIndex || 0;
    setCurrentQuestionIndex(startIdx);
    setTimeout(() => startQuestion(startIdx), 1000);
  };

  const startQuestion = async (qIndex) => {
    const question = session?.questions?.[qIndex];
    if (!question) return;
    setTranscript(''); setInterimTranscript(''); transcriptRef.current = ''; accumulatedTranscriptRef.current = '';
    setTypedAnswer(''); setShowTypeFallback(false);
    speechDetectedRef.current = false;
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    // Reset coding state
    setCodeEditorValue(''); setCodeOutput(''); setCodeLanguage('python');
    setIsSpeaking(true);
    await speakQuestion(question.question);
    setIsSpeaking(false);
    if (question.category === 'coding') {
      // Coding questions: start timer but skip voice recording / MediaRecorder
      setTimeLeft(question.time_limit || 300);
      setIsRecording(true);
      isRecordingRef.current = true;
      questionStartTimeRef.current = Date.now();
      autoSubmitRef.current = false;
    } else {
      startRecording(question.time_limit, qIndex);
    }
  };

  const startRecording = (timeLimit, qIndex) => {
    setTimeLeft(timeLimit);
    setIsRecording(true);
    isRecordingRef.current = true;
    questionStartTimeRef.current = Date.now();
    autoSubmitRef.current = false;
    videoChunksRef.current = [];
    speechDetectedRef.current = false;

    // Start video recording
    if (mediaStream) {
      try {
        const mimeTypes = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4'];
        let selectedMime = '';
        for (const mime of mimeTypes) {
          if (MediaRecorder.isTypeSupported(mime)) { selectedMime = mime; break; }
        }
        const recorderOptions = selectedMime ? { mimeType: selectedMime, videoBitsPerSecond: 500000 } : { videoBitsPerSecond: 500000 };
        const mr = new MediaRecorder(mediaStream, recorderOptions);
        mr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            videoChunksRef.current.push(e.data);
          }
        };
        mr.onerror = (e) => { console.warn('MediaRecorder error:', e); };
        mr.start(1000);
        mediaRecorderRef.current = mr;
      } catch (e) {
        console.warn('MediaRecorder unavailable:', e);
      }
    }

    // Start speech recognition
    const recognition = getSpeechRecognition();
    if (recognition) {
      recognition.continuous = true;
      recognition.interimResults = true;
      // Use calibrated language from voice calibration
      recognition.lang = calibrationLangRef.current || 'en-US';
      recognition.onresult = (event) => {
        speechDetectedRef.current = true;
        let interim = '', sessionFinal = '';
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) sessionFinal += event.results[i][0].transcript + ' ';
          else interim += event.results[i][0].transcript;
        }
        if (sessionFinal) {
          const accumulated = accumulatedTranscriptRef.current;
          const fullText = accumulated ? (accumulated + ' ' + sessionFinal.trim()) : sessionFinal.trim();
          transcriptRef.current = fullText;
          setTranscript(fullText);
        }
        setInterimTranscript(interim);
      };
      recognition.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'service-not-available' || e.error === 'network') {
          setSpeechSupported(false);
          setShowTypeFallback(true);
          toast.error('Voice capture unavailable. Please type your answer below.');
        }
        if (e.error === 'no-speech') {
          // Retry once automatically
          setSpeechRetryCount(prev => {
            if (prev < 1 && isRecordingRef.current) {
              try { recognition.start(); } catch(ex) {}
            } else if (!speechDetectedRef.current) {
              setShowTypeFallback(true);
            }
            return prev + 1;
          });
        }
        if (e.error !== 'no-speech' && e.error !== 'aborted') console.warn('Speech recognition error:', e.error);
      };
      recognition.onend = () => {
        // Save current transcript before restart so it persists across sessions
        if (transcriptRef.current) {
          accumulatedTranscriptRef.current = transcriptRef.current;
        }
        if (isRecordingRef.current && !showTypeFallback) try { recognition.start(); } catch(e) {}
      };
      try { recognition.start(); recognitionRef.current = recognition; } catch(e) {
        setSpeechSupported(false);
        setShowTypeFallback(true);
      }

      // Auto-show typing fallback — adjusted based on voice calibration
      const fallbackDelay = voiceCalibration?.clarity_score >= 60 ? 15000 :
                            voiceCalibration?.clarity_score >= 40 ? 12000 : 8000;
      fallbackTimerRef.current = setTimeout(() => {
        if (!speechDetectedRef.current && isRecordingRef.current) {
          setShowTypeFallback(true);
          toast.warning('No voice detected. You can type your answer below.');
        }
      }, fallbackDelay);
    } else {
      // No speech recognition API at all (Firefox, older Safari)
      setSpeechSupported(false);
      setShowTypeFallback(true);
    }
  };

  const handleDoneSpeaking = useCallback(async () => {
    if (!isRecordingRef.current && !autoSubmitRef.current) return;
    isRecordingRef.current = false;
    setIsRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    if (recognitionRef.current) try { recognitionRef.current.stop(); } catch(e) {}
    recognitionRef.current = null;

    // Stop video recording and wait for final data chunk
    const videoReady = new Promise((resolve) => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        const recorder = mediaRecorderRef.current;
        try { recorder.requestData(); } catch (e) {}
        recorder.onstop = () => { setTimeout(resolve, 500); };
        recorder.stop();
        setTimeout(resolve, 3000);
      } else {
        resolve();
      }
    });
    await videoReady;

    // Save video chunks for later submission
    stoppedVideoChunksRef.current = [...videoChunksRef.current];
    stoppedMimeTypeRef.current = mediaRecorderRef.current?.mimeType || 'video/webm';

    const finalText = transcriptRef.current?.trim() || typedAnswer?.trim() || '';
    const timeTaken = Math.round((Date.now() - (questionStartTimeRef.current || Date.now())) / 1000);
    setReviewTimeTaken(timeTaken);

    if (!finalText) {
      if (!showTypeFallback) {
        setShowTypeFallback(true);
        setIsRecording(true);
        isRecordingRef.current = true;
        toast.error('No speech detected. Please type your answer below.');
        return;
      }
      toast.error('Please provide an answer before submitting.');
      return;
    }

    setReviewText(finalText);
    setShowReviewModal(true);
  }, [typedAnswer, showTypeFallback]);

  const submitReviewedAnswer = useCallback(async () => {
    if (!currentQuestion) return;
    const finalText = reviewText.trim();
    if (!finalText) {
      toast.error('Answer cannot be empty.');
      return;
    }

    try {
      setSubmitting(true);
      setShowReviewModal(false);

      // Upload video
      if (stoppedVideoChunksRef.current.length > 0) {
        const videoBlob = new Blob(stoppedVideoChunksRef.current, { type: stoppedMimeTypeRef.current });
        if (videoBlob.size > 500) {
          try { await uploadInterviewVideo(token, videoBlob, currentQuestionIndex); } catch (e) { console.warn('Video upload failed:', e); }
        }
      }
      stoppedVideoChunksRef.current = [];
      videoChunksRef.current = [];
      mediaRecorderRef.current = null;

      const result = await submitAnswer(token, {
        question_id: currentQuestion.id,
        question: currentQuestion.question,
        category: currentQuestion.category,
        answer_text: finalText,
        time_taken: reviewTimeTaken
      });

      setAnswers(prev => [...prev, { question: currentQuestion.question, answer: finalText, score: result.score, feedback: result.feedback }]);
      toast.success(`Answer submitted! Score: ${result.score}/100`);
      setTranscript(''); setInterimTranscript(''); transcriptRef.current = ''; accumulatedTranscriptRef.current = '';
      setTypedAnswer(''); setShowTypeFallback(false);
      setReviewText('');

      if (currentQuestionIndex < session.questions.length - 1) {
        const nextIdx = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIdx);
        setTimeout(() => startQuestion(nextIdx), 2000);
      } else {
        await handleCompleteInterview();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit answer');
    } finally { setSubmitting(false); }
  }, [currentQuestionIndex, session, token, reviewText, reviewTimeTaken]);

  const handleReRecord = useCallback(() => {
    setShowReviewModal(false);
    setReviewText('');
    setTranscript(''); setInterimTranscript(''); transcriptRef.current = ''; accumulatedTranscriptRef.current = '';
    setTypedAnswer(''); setShowTypeFallback(false);
    stoppedVideoChunksRef.current = [];
    videoChunksRef.current = [];
    // Restart recording for the same question
    startQuestion(currentQuestionIndex);
  }, [currentQuestionIndex]);

  const handleCompleteInterview = async () => {
    try {
      await completeInterview(token);
      // Set completed stage IMMEDIATELY — even if results fetch fails
      setStage('completed');
      if (mediaStream) mediaStream.getTracks().forEach(t => t.stop());
      toast.success('Interview completed!');
      try {
        const r = await getInterviewResults(token);
        setResults(r);
      } catch (e) {
        // Results will load on page refresh — don't block the "Thank you" screen
        console.warn('Could not fetch results immediately:', e);
      }
    } catch (err) {
      // Even if completeInterview API fails, check if backend already marked it completed
      try {
        const session = await getInterviewSession(token);
        if (session.status === 'completed') {
          setStage('completed');
          if (mediaStream) mediaStream.getTracks().forEach(t => t.stop());
          try { const r = await getInterviewResults(token); setResults(r); } catch(e) {}
          return;
        }
      } catch(e) {}
      toast.error('Failed to complete interview. Please try again.');
    }
  };

  // ===== CODING LAB FUNCTIONS =====
  const runCode = async () => {
    if (!codeEditorValue.trim()) {
      toast.error('Write some code before running');
      return;
    }
    setCodeRunning(true);
    setCodeOutput('');
    try {
      const response = await fetch(`${API_BASE}/api/execute-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Interview-Token': token },
        credentials: 'include',
        body: JSON.stringify({ language: codeLanguage, code: codeEditorValue })
      });
      const data = await response.json();
      if (data.status === 'compile_error') {
        setCodeOutput(`Compilation Error:\n${data.error}`);
      } else if (data.status === 'timeout') {
        setCodeOutput('Execution timed out (10s limit)');
      } else if (data.status === 'runtime_error') {
        setCodeOutput(`${data.output || ''}${data.error ? '\nError:\n' + data.error : ''}`);
      } else {
        setCodeOutput(data.output || '(No output)');
      }
    } catch (e) {
      setCodeOutput('Failed to execute code: ' + e.message);
    } finally {
      setCodeRunning(false);
    }
  };

  const submitCodingAnswer = async () => {
    if (!codeEditorValue.trim()) {
      toast.error('Please write some code before submitting');
      return;
    }
    isRecordingRef.current = false;
    setIsRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);

    const currentQ = session?.questions?.[currentQuestionIndex];
    if (!currentQ) return;
    const timeTaken = Math.round((Date.now() - (questionStartTimeRef.current || Date.now())) / 1000);
    const answerText = `[Language: ${codeLanguage}]\n\n${codeEditorValue}\n\n[Output]:\n${codeOutput || 'Not executed'}`;

    try {
      setSubmitting(true);
      const result = await submitAnswer(token, {
        question_id: currentQ.id,
        question: currentQ.question,
        category: currentQ.category,
        answer_text: answerText,
        time_taken: timeTaken
      });
      setAnswers(prev => [...prev, { question: currentQ.question, answer: answerText, score: result.score, feedback: result.feedback }]);
      toast.success(`Code submitted! Score: ${result.score}/100`);
      setCodeEditorValue(''); setCodeOutput(''); setCodeLanguage('python');

      if (currentQuestionIndex < session.questions.length - 1) {
        const nextIdx = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIdx);
        setTimeout(() => startQuestion(nextIdx), 2000);
      } else {
        await handleCompleteInterview();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit code');
    } finally { setSubmitting(false); }
  };

  const LANGUAGE_OPTIONS = [
    { value: 'python', label: 'Python', monacoId: 'python' },
    { value: 'javascript', label: 'JavaScript', monacoId: 'javascript' },
    { value: 'java', label: 'Java', monacoId: 'java' },
    { value: 'cpp', label: 'C++', monacoId: 'cpp' },
    { value: 'sql', label: 'SQL', monacoId: 'sql' },
  ];

  const getMonacoLanguage = (lang) => {
    const found = LANGUAGE_OPTIONS.find(l => l.value === lang);
    return found ? found.monacoId : 'plaintext';
  };

  const getDefaultCode = (lang) => {
    const templates = {
      python: '# Write your solution here\n\n',
      javascript: '// Write your solution here\n\n',
      java: 'public class Main {\n    public static void main(String[] args) {\n        // Write your solution here\n        \n    }\n}\n',
      cpp: '#include <iostream>\nusing namespace std;\n\nint main() {\n    // Write your solution here\n    \n    return 0;\n}\n',
      sql: '-- Write your SQL query here\n-- Example: CREATE TABLE, INSERT, SELECT\n\nCREATE TABLE IF NOT EXISTS employees (\n    id INTEGER PRIMARY KEY,\n    name TEXT NOT NULL,\n    department TEXT,\n    salary REAL\n);\n\n-- INSERT sample data\nINSERT INTO employees (name, department, salary) VALUES\n    (\'Alice\', \'Engineering\', 95000),\n    (\'Bob\', \'Marketing\', 72000),\n    (\'Charlie\', \'Engineering\', 88000);\n\n-- Write your query below\nSELECT * FROM employees;\n',
    };
    return templates[lang] || '// Write your solution here\n';
  };

  // ===== RENDER =====
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Toaster richColors position="top-right" /><Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Toaster richColors position="top-right" />
      <Card className="max-w-md w-full"><CardContent className="pt-6 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2" data-testid="interview-error-title">Session Error</h2>
        <p className="text-muted-foreground">{error}</p>
      </CardContent></Card>
    </div>
  );

  if (stage === 'completed') {
    const handleRecompute = isRecruiterView ? async () => {
      try {
        toast.loading('Recomputing analysis...');
        await recomputeInterviewAnalysis(token);
        const r = await getInterviewResults(token);
        setResults(r);
        toast.dismiss();
        toast.success('Analysis recomputed successfully');
      } catch (e) {
        toast.dismiss();
        toast.error('Failed to recompute analysis');
      }
    } : null;

    if ((isRecruiterView || session?.show_results_to_candidate) && !results?.results_hidden) {
      return <ResultsPage session={session} results={results} onRecompute={handleRecompute} token={token} />;
    }
    return <ThankYouPage session={session} candidateName={results?.candidate_name || session?.candidate_name || candidateInfo.candidate_name} jobTitle={results?.job_title || session?.job_title} />;
  }

  if (stage === 'info') return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Toaster richColors position="top-right" />
      <Card className="w-full max-w-md" data-testid="interview-info-card">
        <CardHeader>
          <CardTitle className="text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Video Interview</CardTitle>
          <p className="text-center text-muted-foreground">{session?.job_title}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input id="name" data-testid="candidate-name-input" value={candidateInfo.candidate_name}
              onChange={(e) => setCandidateInfo(p => ({ ...p, candidate_name: e.target.value }))} placeholder="John Doe" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input id="email" type="email" data-testid="candidate-email-input" value={candidateInfo.candidate_email}
              onChange={(e) => setCandidateInfo(p => ({ ...p, candidate_email: e.target.value }))} placeholder="john@example.com" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone *</Label>
            <Input id="phone" data-testid="candidate-phone-input" value={candidateInfo.candidate_phone}
              onChange={(e) => setCandidateInfo(p => ({ ...p, candidate_phone: e.target.value }))} placeholder="+1234567890" />
          </div>
          <div className="bg-muted/50 p-3 rounded-lg text-sm space-y-2">
            <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /><span className="font-medium">Interview Guidelines:</span></div>
            <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
              <li>Camera & microphone access required — video will be recorded</li>
              <li>AI reads each question aloud, then recording starts automatically</li>
              <li>Speak your answer clearly — live transcription is shown on screen</li>
              <li>Click "Stop & Submit" when done or wait for the timer</li>
              <li>{session?.total_questions || 8} questions total</li>
              <li>Tab switching will be detected and recorded as violations</li>
            </ul>
          </div>
          <Button className="w-full" onClick={handleStartInterview} data-testid="start-interview-btn">
            Start Interview<ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  if (stage === 'terms') return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Toaster richColors position="top-right" />
      <Card className="w-full max-w-2xl" data-testid="interview-terms-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-center mb-2">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-center text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Interview Guidelines & Agreement
          </CardTitle>
          <p className="text-center text-sm text-muted-foreground mt-1">
            Please review the following guidelines carefully before proceeding
          </p>
        </CardHeader>
        <CardContent className="space-y-5">

          {/* What to Expect */}
          <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-primary" />
              What to Expect
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <MessageSquare className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                <span><strong>{session?.total_questions || 8} questions</strong> — technical, work setup & culture fit</span>
              </div>
              <div className="flex items-start gap-2">
                <Volume2 className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                <span>AI reads each question aloud, then recording begins</span>
              </div>
              <div className="flex items-start gap-2">
                <Timer className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                <span>Each question has a <strong>time limit</strong> displayed on screen</span>
              </div>
              <div className="flex items-start gap-2">
                <Video className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                <span>Your <strong>video & audio</strong> are recorded throughout</span>
              </div>
            </div>
          </div>

          {/* Do's and Don'ts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Do's */}
            <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-4 space-y-2.5">
              <h3 className="text-sm font-semibold text-green-600 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Do's
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                  <span>Use <strong>Google Chrome</strong> for the best experience</span>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                  <span>Sit in a <strong>quiet, well-lit</strong> environment</span>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                  <span><strong>Speak clearly</strong> — a live transcript appears on screen</span>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                  <span>If voice isn't detected, a <strong>text box</strong> will appear to type your answer</span>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                  <span>Click <strong>"Stop & Submit"</strong> when done, or let the timer auto-submit</span>
                </li>
              </ul>
            </div>

            {/* Don'ts */}
            <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4 space-y-2.5">
              <h3 className="text-sm font-semibold text-red-500 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                Don'ts
              </h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2 text-muted-foreground">
                  <XCircle className="h-3.5 w-3.5 mt-0.5 text-red-400 shrink-0" />
                  <span><strong>Do not switch tabs</strong> — all tab switches are detected and recorded as violations</span>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <XCircle className="h-3.5 w-3.5 mt-0.5 text-red-400 shrink-0" />
                  <span><strong>Do not copy or paste</strong> text during the interview</span>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <XCircle className="h-3.5 w-3.5 mt-0.5 text-red-400 shrink-0" />
                  <span><strong>Do not leave</strong> the interview midway — it must be completed in one sitting</span>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <XCircle className="h-3.5 w-3.5 mt-0.5 text-red-400 shrink-0" />
                  <span><strong>Do not use incognito</strong> or private browsing mode</span>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <XCircle className="h-3.5 w-3.5 mt-0.5 text-red-400 shrink-0" />
                  <span><strong>Do not rush</strong> — take a moment to think before answering</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Important Notice */}
          <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong className="text-yellow-600">Proctoring Notice:</strong> This interview is monitored. Tab switches, copy-paste attempts, 
                and other suspicious activities are automatically detected and will be included in your assessment report. 
                Your recruiter will have full visibility of all violations.
              </p>
            </div>
          </div>

          {/* Agreement Checkbox */}
          <Separator />
          <label className="flex items-start gap-3 cursor-pointer select-none group" data-testid="terms-agreement-label">
            <input
              type="checkbox"
              checked={termsAgreed}
              onChange={(e) => setTermsAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-2 border-muted-foreground/40 accent-primary cursor-pointer"
              data-testid="terms-agreement-checkbox"
            />
            <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
              I have read and understood the guidelines above. I agree to be recorded and monitored during this interview, and I will not engage in any prohibited activities.
            </span>
          </label>

          {/* Continue Button */}
          <Button
            className="w-full"
            onClick={() => setStage('device_check')}
            disabled={!termsAgreed}
            data-testid="terms-continue-btn"
          >
            I Understand & Continue
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  if (stage === 'device_check') return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Toaster richColors position="top-right" />
      <Card className="w-full max-w-2xl" data-testid="device-check-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-center mb-2">
            <div className="w-12 h-12 rounded-full bg-cyan-500/10 flex items-center justify-center">
              <Mic className="h-6 w-6 text-cyan-500" />
            </div>
          </div>
          <CardTitle className="text-center text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Device Check
          </CardTitle>
          <p className="text-center text-sm text-muted-foreground mt-1">
            Let's make sure your camera, microphone, and voice capture are working
          </p>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Camera Check */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Video className="h-4 w-4 text-cyan-500" />
                Camera
              </h3>
              {cameraOk ? (
                <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Working
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Checking...
                </Badge>
              )}
            </div>
            <div className="relative rounded-lg overflow-hidden bg-black aspect-video max-h-[180px]">
              <video
                ref={deviceVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                data-testid="device-check-video"
              />
              {!cameraOk && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-white" />
                </div>
              )}
            </div>
          </div>

          {/* Mic Check */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Mic className="h-4 w-4 text-cyan-500" />
                Microphone
              </h3>
              {micOk ? (
                <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Detected
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-muted-foreground">
                  Speak to test...
                </Badge>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Speak something — the bar should move</p>
              <div className="h-4 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-100 ${
                    micLevel > 30 ? 'bg-emerald-500' : micLevel > 10 ? 'bg-amber-500' : 'bg-slate-400'
                  }`}
                  style={{ width: `${Math.max(2, micLevel)}%` }}
                  data-testid="mic-level-bar"
                />
              </div>
            </div>
          </div>

          {/* Speech Recognition Check */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-cyan-500" />
                Voice Capture
              </h3>
              {speechOk ? (
                <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Working
                </Badge>
              ) : speechTestResult === 'NOT_SUPPORTED' ? (
                <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 gap-1">
                  <AlertTriangle className="h-3 w-3" /> Not Supported
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 text-muted-foreground">
                  Needs testing
                </Badge>
              )}
            </div>
            <div className="p-4 rounded-lg border bg-muted/20 space-y-3">
              {speechTestResult === 'NOT_SUPPORTED' ? (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">
                      Your browser doesn't support voice capture. <strong>For best experience, use Google Chrome.</strong> 
                      You can still proceed — you'll type your answers instead.
                    </p>
                  </div>
                </div>
              ) : !speechTesting && !speechOk ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Click the button below and say: <strong>"Hello, I am ready for the interview"</strong>
                  </p>
                  <Button
                    onClick={testSpeechRecognition}
                    variant="outline"
                    className="w-full"
                    data-testid="test-speech-btn"
                  >
                    <Mic className="h-4 w-4 mr-2" />
                    {speechRetryCount > 0 ? 'Retry Voice Test' : 'Start Voice Test'}
                  </Button>
                  {speechTestResult === 'NO_SPEECH' && (
                    <p className="text-xs text-amber-500 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> No speech detected. Ensure your microphone is on and try again.
                    </p>
                  )}
                  {speechTestResult.startsWith('ERROR:') && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <XCircle className="h-3 w-3" /> {speechTestResult}. Try using Google Chrome for best compatibility.
                    </p>
                  )}
                </div>
              ) : speechTesting ? (
                <div className="space-y-2 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-sm font-medium text-red-500">Listening...</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Say: "Hello, I am ready for the interview"</p>
                  {speechTestResult && (
                    <div className="p-2 bg-background rounded border mt-2">
                      <p className="text-sm font-mono">{speechTestResult}</p>
                    </div>
                  )}
                </div>
              ) : speechOk ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-emerald-500">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-medium">Voice captured successfully!</span>
                  </div>
                  <div className="p-2 bg-background rounded border">
                    <p className="text-sm font-mono text-muted-foreground">"{speechTestResult}"</p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="space-y-3">
            {(cameraOk && micOk && speechOk) ? (
              <Button
                className="w-full"
                onClick={handleDeviceCheckProceed}
                data-testid="device-check-proceed-btn"
              >
                All Good — Start Interview
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <div className="space-y-2">
                <Button className="w-full" disabled variant="outline" data-testid="device-check-blocked-btn">
                  {!cameraOk ? 'Camera check required' : !micOk ? 'Microphone check required' : 'Voice capture test required'}
                </Button>
                {speechTestResult === 'NOT_SUPPORTED' && (
                  <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Chrome className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-500">
                        <strong>Voice capture is required.</strong> Your browser does not support speech recognition. 
                        Please switch to <strong>Google Chrome</strong> to proceed with the interview.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Report Issue */}
            {!showIssueForm ? (
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => setShowIssueForm(true)}
                data-testid="show-report-issue-btn"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Having trouble? Report an issue
              </Button>
            ) : (
              <div className="p-4 rounded-lg border border-red-500/20 bg-red-500/5 space-y-3" data-testid="issue-report-form">
                <h4 className="text-sm font-semibold flex items-center gap-2 text-red-500">
                  <AlertTriangle className="h-4 w-4" /> Report Issue
                </h4>
                <Textarea
                  placeholder="Describe what's not working (e.g., 'Microphone not detecting voice on Safari', 'Camera is black')"
                  value={issueText}
                  onChange={(e) => setIssueText(e.target.value)}
                  rows={3}
                  data-testid="issue-text-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleReportIssue}
                    disabled={reportingIssue || !issueText.trim()}
                    size="sm"
                    variant="destructive"
                    data-testid="submit-issue-btn"
                  >
                    {reportingIssue ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                    Send Report
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowIssueForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (stage === 'permissions') return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Toaster richColors position="top-right" />
      <Card className="w-full max-w-md" data-testid="interview-permissions-card">
        <CardHeader><CardTitle className="text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>Grant Permissions</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4"><Video className="h-12 w-12 text-primary" /></div>
            <p className="text-muted-foreground">Grant camera and microphone access to continue. Your video will be recorded.</p>
          </div>
          <div className="flex justify-center gap-4">
            <div className={`p-3 rounded-lg ${hasPermissions ? 'bg-green-500/10' : 'bg-muted'}`}><Video className={`h-6 w-6 ${hasPermissions ? 'text-green-500' : 'text-muted-foreground'}`} /></div>
            <div className={`p-3 rounded-lg ${hasPermissions ? 'bg-green-500/10' : 'bg-muted'}`}><Mic className={`h-6 w-6 ${hasPermissions ? 'text-green-500' : 'text-muted-foreground'}`} /></div>
          </div>
          <Button className="w-full" onClick={handlePermissionsGranted} data-testid="grant-permissions-btn">
            {hasPermissions ? 'Begin Interview' : 'Grant Access & Begin'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // VOICE CALIBRATION STAGE
  if (stage === 'voice_calibration') return (
    <VoiceCalibration
      token={token}
      onComplete={handleCalibrationComplete}
      mediaStream={mediaStream}
    />
  );

  // INTERVIEW STAGE
  const progress = ((currentQuestionIndex) / (session?.questions?.length || 1)) * 100;
  const displayTranscript = transcript + (interimTranscript ? ' ' + interimTranscript : '');

  return (
    <div className="min-h-screen bg-background p-4 overflow-x-hidden">
      <Toaster richColors position="top-right" />
      <AlertDialog open={showViolationWarning} onOpenChange={setShowViolationWarning}>
        <AlertDialogContent><AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" />Warning: Tab Switch Detected</AlertDialogTitle>
          <AlertDialogDescription>You switched away from the interview. This has been recorded. Violations: {violations}</AlertDialogDescription>
        </AlertDialogHeader>
        <Button onClick={() => setShowViolationWarning(false)} data-testid="dismiss-violation-btn">Continue</Button>
        </AlertDialogContent>
      </AlertDialog>

      {/* Answer Review Modal */}
      <AlertDialog open={showReviewModal} onOpenChange={(open) => { if (!open && !submitting) setShowReviewModal(false); }}>
        <AlertDialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="review-answer-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <FileText className="h-5 w-5 text-primary" />
              Review Your Answer
            </AlertDialogTitle>
            <AlertDialogDescription>
              Review and edit your captured answer below. Fix any speech-to-text errors before submitting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-muted/30 rounded-lg border">
              <p className="text-xs text-muted-foreground mb-1 font-medium">Question:</p>
              <p className="text-sm leading-relaxed">{currentQuestion?.question}</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground font-medium">Your Answer:</p>
                <span className="text-[10px] text-muted-foreground">{reviewText.length} characters</span>
              </div>
              <Textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                className="min-h-[160px] text-sm leading-relaxed resize-y"
                placeholder="Your answer will appear here..."
                data-testid="review-answer-textarea"
                autoFocus
              />
              <p className="text-[11px] text-muted-foreground">
                You can freely edit the text above to correct any speech recognition errors.
              </p>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleReRecord}
              disabled={submitting}
              className="gap-1.5"
              data-testid="re-record-btn"
            >
              <Mic className="h-3.5 w-3.5" />
              Re-record
            </Button>
            <Button
              onClick={submitReviewedAnswer}
              disabled={submitting || !reviewText.trim()}
              className="flex-1 gap-1.5"
              data-testid="submit-reviewed-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>
              ) : (
                <><CheckCircle2 className="h-4 w-4" /> Submit Answer</>
              )}
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{session?.job_title}</h1>
            <p className="text-sm text-muted-foreground">Question {currentQuestionIndex + 1} of {session?.questions?.length}</p>
          </div>
          <div className="flex items-center gap-3">
            {violations > 0 && <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />{violations}</Badge>}
            {copyPasteViolations > 0 && <Badge variant="destructive" className="gap-1"><Ban className="h-3 w-3" />{copyPasteViolations} paste</Badge>}
            {isRecording && (
              <Badge variant="outline" className={`gap-1 ${timeLeft <= 0 ? 'text-orange-500 border-orange-500/50 bg-orange-500/10' : 'text-red-500 border-red-500/30'} animate-pulse`}>
                <Clock className="h-3 w-3" />
                {timeLeft <= 0
                  ? `+${Math.floor(Math.abs(timeLeft) / 60)}:${(Math.abs(timeLeft) % 60).toString().padStart(2, '0')} overtime`
                  : `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`}
              </Badge>
            )}
          </div>
        </div>
        <Progress value={progress} className="h-2 mb-6" data-testid="interview-progress" />

        {/* Overtime warning banner */}
        {isRecording && timeLeft <= 0 && (
          <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg flex items-center gap-2" data-testid="overtime-banner">
            <Timer className="h-4 w-4 text-orange-500" />
            <span className="text-sm text-orange-600 font-medium">Time's up! You're in overtime. Submit when ready.</span>
          </div>
        )}

        {currentQuestion?.category === 'coding' ? (
          /* ===== CODING QUESTION LAYOUT ===== */
          <div className="space-y-4" data-testid="coding-lab">
            {/* Question header */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-violet-500/10 text-violet-500 border-violet-500/20 gap-1">
                    <Code2 className="h-3 w-3" /> Coding
                  </Badge>
                  <Badge variant="outline" className="text-xs"><Clock className="h-3 w-3 mr-1" />{currentQuestion?.time_limit}s</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-base leading-relaxed font-medium" data-testid="current-question-text">{currentQuestion?.question}</p>
              </CardContent>
            </Card>

            {isSpeaking && (
              <div className="flex items-center gap-2 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                <div className="flex gap-1">{[4,6,3,5,4].map((h,i)=> <div key={i} className="w-1.5 bg-blue-500 rounded-full animate-pulse" style={{height:`${h*4}px`, animationDelay:`${i*100}ms`}}/>)}</div>
                <span className="text-sm text-blue-500">AI reading question aloud...</span>
              </div>
            )}

            {isRecording && (
              <>
                {/* Language selector + Run button */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium">Language:</Label>
                    <select
                      value={codeLanguage}
                      onChange={(e) => {
                        setCodeLanguage(e.target.value);
                        if (!codeEditorValue.trim() || codeEditorValue === getDefaultCode(codeLanguage)) {
                          setCodeEditorValue(getDefaultCode(e.target.value));
                        }
                      }}
                      className="h-9 px-3 rounded-md border bg-background text-sm"
                      data-testid="code-language-select"
                    >
                      {LANGUAGE_OPTIONS.map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={runCode}
                    disabled={codeRunning || !codeEditorValue.trim()}
                    className="gap-1"
                    data-testid="run-code-btn"
                  >
                    {codeRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                    {codeRunning ? 'Running...' : 'Run Code'}
                  </Button>
                </div>

                {/* Code Editor */}
                <div className="rounded-lg border overflow-hidden" data-testid="code-editor-container">
                  <Editor
                    height="350px"
                    language={getMonacoLanguage(codeLanguage)}
                    value={codeEditorValue || getDefaultCode(codeLanguage)}
                    onChange={(value) => setCodeEditorValue(value || '')}
                    theme="vs-dark"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 14,
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      automaticLayout: true,
                      tabSize: 4,
                      padding: { top: 12 },
                    }}
                  />
                </div>

                {/* Output Panel */}
                <div className="rounded-lg border bg-zinc-950 overflow-hidden" data-testid="code-output-panel">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800">
                    <Terminal className="h-3.5 w-3.5 text-zinc-400" />
                    <span className="text-xs text-zinc-400 font-medium">Output</span>
                  </div>
                  <pre className="p-3 text-sm text-zinc-300 font-mono min-h-[80px] max-h-[200px] overflow-y-auto whitespace-pre-wrap">
                    {codeRunning ? 'Running...' : (codeOutput || 'Run your code to see output here')}
                  </pre>
                </div>

                {/* Submit Button */}
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={submitCodingAnswer}
                  disabled={submitting || !codeEditorValue.trim()}
                  data-testid="submit-code-btn"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing...</> : <><Code2 className="h-4 w-4 mr-2" />Submit Code</>}
                </Button>
              </>
            )}

            {submitting && !isRecording && (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary mr-3" /><span className="text-muted-foreground">AI evaluating your code...</span></div>
            )}
            {answers.length > 0 && answers.length === currentQuestionIndex && !isRecording && !submitting && !isSpeaking && (
              <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                <div className="flex items-center gap-2 text-green-600 mb-1"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-medium">Scored: {answers[answers.length-1]?.score}/100</span></div>
                <p className="text-xs text-muted-foreground">Next question loading...</p>
              </div>
            )}
          </div>
        ) : (
          /* ===== VOICE/VIDEO QUESTION LAYOUT (existing) ===== */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-4">
              <div className="aspect-video bg-muted rounded-lg overflow-hidden relative">
                <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                {isRecording && <div className="absolute top-4 left-4 flex items-center gap-2"><div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" /><span className="text-xs text-white bg-black/50 px-2 py-1 rounded">Recording</span></div>}
                {isSpeaking && <div className="absolute top-4 left-4 flex items-center gap-2"><div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" /><span className="text-xs text-white bg-black/50 px-2 py-1 rounded">AI reading question...</span></div>}
                {submitting && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><div className="flex items-center gap-2 text-white"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-sm">Analyzing...</span></div></div>}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Badge className="bg-primary/10 text-primary border-primary/20">
                  {currentQuestion?.category === 'technical' ? 'Technical' : currentQuestion?.category === 'work_setup' ? 'Work Setup' : 'Culture Fit'}
                </Badge>
                <Badge variant="outline" className="text-xs"><Clock className="h-3 w-3 mr-1" />{currentQuestion?.time_limit}s</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-base leading-relaxed font-medium" data-testid="current-question-text">{currentQuestion?.question}</p>
              {isSpeaking && (
                <div className="flex items-center gap-2 p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                  <div className="flex gap-1">{[4,6,3,5,4].map((h,i)=> <div key={i} className="w-1.5 bg-blue-500 rounded-full animate-pulse" style={{height:`${h*4}px`, animationDelay:`${i*100}ms`}}/>)}</div>
                  <span className="text-sm text-blue-500">Listening... Recording starts after.</span>
                </div>
              )}
              {isRecording && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-2 bg-red-500/5 border border-red-500/20 rounded-lg">
                    <div className="flex gap-0.5 items-end h-4">{[40,80,60,100,50].map((h,i)=> <div key={i} className="w-1 bg-red-500 rounded-full animate-pulse" style={{height:`${h}%`, animationDelay:`${i*50}ms`}}/>)}</div>
                    <span className="text-sm text-red-500 font-medium"><Mic className="h-3.5 w-3.5 inline mr-1" />Recording{speechSupported ? ' — Speak now' : ''}</span>
                  </div>

                  {/* Live transcript (speech-to-text) */}
                  {speechSupported && (
                    <div className="min-h-[80px] max-h-[150px] overflow-y-auto p-3 bg-muted/30 border rounded-lg text-sm select-none"
                      style={{ userSelect: 'none', WebkitUserSelect: 'none' }} data-testid="live-transcript">
                      {displayTranscript ? <p className="leading-relaxed">{transcript}{interimTranscript && <span className="text-muted-foreground italic"> {interimTranscript}</span>}</p> : <p className="text-muted-foreground italic">Listening for your voice...</p>}
                    </div>
                  )}

                  {/* Typing fallback */}
                  {showTypeFallback && (
                    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="type-fallback">
                      <div className="p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-xs text-yellow-600">
                        {speechSupported
                          ? "Voice not detected. You can type your answer below while still being recorded on video."
                          : "Speech-to-text is not supported in your browser. Please type your answer below."}
                      </div>
                      <Textarea
                        value={typedAnswer}
                        onChange={(e) => setTypedAnswer(e.target.value)}
                        placeholder="Type your answer here..."
                        className="min-h-[100px] resize-none"
                        data-testid="typed-answer-input"
                        autoFocus
                      />
                    </div>
                  )}

                  {/* Show "type instead" link */}
                  {!showTypeFallback && speechSupported && (
                    <button
                      onClick={() => setShowTypeFallback(true)}
                      className="text-xs text-muted-foreground hover:text-primary underline"
                      data-testid="show-type-fallback-btn"
                    >
                      Voice not working? Click here to type your answer instead
                    </button>
                  )}

                  <Button variant="destructive" className="w-full" onClick={handleDoneSpeaking} disabled={submitting} data-testid="stop-submit-btn">
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing...</> :
                      showTypeFallback
                        ? <><Square className="h-4 w-4 mr-2" />Review & Submit Answer</>
                        : <><Square className="h-4 w-4 mr-2" />Done Speaking — Review Answer</>
                    }
                  </Button>
                </div>
              )}
              {submitting && !isRecording && (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary mr-3" /><span className="text-muted-foreground">AI evaluating your answer...</span></div>
              )}
              {answers.length > 0 && answers.length === currentQuestionIndex && !isRecording && !submitting && !isSpeaking && (
                <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-green-600 mb-1"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-medium">Scored: {answers[answers.length-1]?.score}/100</span></div>
                  <p className="text-xs text-muted-foreground">Next question loading...</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}
      </div>
    </div>
  );
};

export default InterviewPage;
